import React, { useState, useMemo, useCallback, useEffect } from "react"
import { Select, Button, Tooltip } from "antd"
import type { DefaultOptionType } from "antd/es/select"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { groupCodebookOptions } from "utils/codebookUtils"
import { CodebookData } from "types/codebook.types"

// Props interface for the component
interface ReportingGlobalFilterProps {
  /** Current filter value from parent configuration */
  filter: string
  /** Callback function to update the global filter value in the parent component */
  setFilter: (filter: string) => void
  /** The codebook data structure to generate filter options from */
  codebook: CodebookData
}

/**
 * Component providing a global filter dropdown for the reporting section.
 * Uses a grouped Select component based on the processed codebook.
 * Compact layout.
 */
const ReportingGlobalFilter: React.FC<ReportingGlobalFilterProps> = ({
  filter,
  setFilter,
  codebook,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false)

  // Handle window resize
  useEffect(() => {
    const handleResize = (): void => {
      const width: number = window.innerWidth
      const smallScreenBreakpoint: number = 1400
      const newIsSmallScreen: boolean = width < smallScreenBreakpoint
      setIsSmallScreen(newIsSmallScreen)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const selectOptions = useMemo((): DefaultOptionType[] => {
    return groupCodebookOptions(codebook)
  }, [codebook])

  const handleFilterChange = useCallback(
    (value: string | undefined): void => {
      const filterValue: string = value || ""
      setFilter(filterValue)
    },
    [setFilter]
  )

  const handleReset = useCallback((): void => {
    setFilter("")
  }, [setFilter])

  const filterOption = useCallback(
    (input: string, option?: DefaultOptionType): boolean => {
      if (!option) return false

      const lowerInput: string = input.toLowerCase()

      // Handle the label which can be ReactNode
      const labelText =
        typeof option.label === "string"
          ? option.label
          : String(option.label || "")

      if (labelText.toLowerCase().includes(lowerInput)) {
        return true
      }

      // Check nested options if they exist
      if (option.options && Array.isArray(option.options)) {
        return option.options.some((opt: DefaultOptionType) => {
          const optLabelText =
            typeof opt.label === "string" ? opt.label : String(opt.label || "")
          return optLabelText.toLowerCase().includes(lowerInput)
        })
      }

      return false
    },
    []
  )

  return (
    <div className="inline-block relative">
      <div className="bg-white px-3 py-3 rounded-b-lg shadow-sm w-[180px] xlplus:w-[250px] flex items-center gap-2">
        {!isSmallScreen && (
          <label className="text-xs font-medium whitespace-nowrap">
            Global Filter:
          </label>
        )}
        <Tooltip
          title="This filter applies to all reports and dashboards."
          placement="bottom"
        >
          <span className="text-gray-400 cursor-help">
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
        </Tooltip>

        <div className="flex-grow min-w-0">
          <Select<string>
            showSearch
            allowClear
            placeholder="Select a filter..."
            optionFilterProp="label"
            className="w-full text-xs"
            options={selectOptions}
            value={filter || undefined}
            onChange={handleFilterChange}
            filterOption={filterOption}
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
            aria-label="Global Filter Selection"
          />
        </div>

        {/* Reset Button (conditionally rendered) */}
        {filter && (
          <Button
            type="text"
            size="small"
            onClick={handleReset}
            className="flex-shrink-0"
            style={{ minWidth: "auto", padding: "0 4px" }}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  )
}

export default ReportingGlobalFilter
